// Phase 2 Task 16 / H-5 — Minimal first-run onboarding tooltip.
//
// DGUV 215-450 Ch.4 (Psychological approaches): "Introduce users to the use of
// new software in a targeted manner, especially during major changes." A full
// in-app tour is over-scoped for the operator workflow, but two discoverability
// gaps justify a one-shot tooltip each:
//   • AutoButton (icon-only morphing CTA — what action it triggers is not obvious)
//   • NavBar pin/cogwheel (operators can pin admin pages into the top nav)
//
// Each tooltip writes a per-key flag to localStorage on dismiss; once seen, it
// never shows again. Renders as a floating overlay positioned next to its
// anchor; an explicit "Verstanden" button + role=dialog + aria-modal mark the
// scope so screen readers can speak it without trapping focus permanently.
import React, { useState, useRef, useEffect } from 'react';
import { theme } from '../theme';

interface FirstRunTooltipProps {
  // Unique localStorage key — bumping the version (v2 suffix etc.) re-shows after a feature change.
  storageKey: string;
  // The element this tooltip points at. Must be focusable; the tooltip uses its
  // bounding rect to position itself just below.
  children: React.ReactNode;
  // Body copy in German, short imperative sentence.
  title: string;
  body: string;
  // Optional placement override (default: below the anchor, left-aligned).
  placement?: 'below' | 'above';
}

const FIRST_RUN_PREFIX = 'smarttix_first_run_';

export const FirstRunTooltip: React.FC<FirstRunTooltipProps> = ({
  storageKey,
  children,
  title,
  body,
  placement = 'below',
}) => {
  const key = FIRST_RUN_PREFIX + storageKey;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(key) === '1'; } catch { return true; }
  });
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [vw, setVw] = useState<number>(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));

  useEffect(() => {
    if (dismissed) return;
    // Measure once on mount; if anchor relocates, the operator dismisses anyway.
    if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
  }, [dismissed]);

  // Why: keep the tooltip inside the viewport when the operator resizes the
  // browser while the box is visible (operator-reported case 2026-05-27: cogwheel
  // anchor on a phone-narrow window had the tooltip cropped off the right edge).
  useEffect(() => {
    if (dismissed) return;
    const onResize = () => {
      setVw(window.innerWidth);
      if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [dismissed]);

  const handleDismiss = () => {
    try { localStorage.setItem(key, '1'); } catch { /* private-mode quota — accept ephemeral dismiss */ }
    setDismissed(true);
  };

  return (
    <>
      <span ref={anchorRef} style={{ display: 'inline-flex' }}>{children}</span>
      {!dismissed && rect && (() => {
        // Clamp horizontal position so the box stays inside the viewport at any
        // window width (the maxWidth=280 box is wider than narrow phone screens
        // would allow if anchored to a right-edge element like the NavBar cogwheel).
        const SAFE_MARGIN = 8;
        const TOOLTIP_MAX_WIDTH = 280;
        const maxLeft = Math.max(SAFE_MARGIN, vw - TOOLTIP_MAX_WIDTH - SAFE_MARGIN);
        const clampedLeft = Math.max(SAFE_MARGIN, Math.min(rect.left, maxLeft));
        return (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby={`${key}-title`}
          aria-describedby={`${key}-body`}
          style={{
            position: 'fixed',
            top: placement === 'below' ? rect.bottom + 8 : Math.max(8, rect.top - 100),
            left: clampedLeft,
            // maxWidth shrinks when viewport is narrower than the default 280px.
            maxWidth: Math.min(TOOLTIP_MAX_WIDTH, vw - 2 * SAFE_MARGIN),
            padding: theme.spacing.md,
            background: theme.colors.navy, color: theme.colors.white,
            borderRadius: theme.borderRadius.md,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            fontFamily: theme.font.family, fontSize: 13, lineHeight: 1.4,
            zIndex: 9999,
            animation: 'stx-fadeIn 0.18s ease',
          }}
        >
          <div id={`${key}-title`} style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
          <div id={`${key}-body`} style={{ opacity: 0.95, marginBottom: theme.spacing.md }}>{body}</div>
          <button
            onClick={handleDismiss}
            autoFocus
            style={{
              background: theme.colors.white, color: theme.colors.navy,
              border: 'none', borderRadius: theme.borderRadius.sm,
              padding: '6px 14px', fontSize: 12, fontWeight: 600,
              fontFamily: theme.font.family, cursor: 'pointer',
            }}
          >Verstanden</button>
        </div>
        );
      })()}
    </>
  );
};
